import React, { useEffect, useRef, useState } from "react";
import "./IsztarValidationCard.css";

const IsztarValidationCard = ({ loading = false, validation = null }) => {
  const [copiedCode, setCopiedCode] = useState(null);
  const [copyErrorCode, setCopyErrorCode] = useState(null);
  const resetTimerRef = useRef(null);

  useEffect(() => {
    setCopiedCode(null);
    setCopyErrorCode(null);
  }, [validation]);

  useEffect(
    () => () => {
      if (resetTimerRef.current) clearTimeout(resetTimerRef.current);
    },
    [],
  );

  const handleCopy = async (code) => {
    try {
      await navigator.clipboard.writeText(code);
      setCopiedCode(code);
      setCopyErrorCode(null);
      if (resetTimerRef.current) clearTimeout(resetTimerRef.current);
      resetTimerRef.current = setTimeout(() => setCopiedCode(null), 2000);
    } catch {
      setCopiedCode(null);
      setCopyErrorCode(code);
    }
  };

  if (loading) {
    return (
      <div
        className="isztar-validation isztar-validation--loading"
        role="status"
        aria-live="polite"
      >
        <span className="spinner-border spinner-border-sm" aria-hidden="true" />
        <span>Sprawdzanie zgodności kodu z nazwą produktu…</span>
      </div>
    );
  }

  if (!validation) return null;

  const isError = typeof validation.error === "string";
  const isValid = validation.result === "TAK";
  const isIncomplete = validation.result === "NIEPEŁNE DANE";
  const variant = isError
    ? "error"
    : isValid
      ? "valid"
      : isIncomplete
        ? "incomplete"
        : "invalid";
  const iconClass = isError
    ? "bi-exclamation-circle"
    : isValid
      ? "bi-check-circle-fill"
      : isIncomplete
        ? "bi-info-circle-fill"
        : "bi-x-circle-fill";
  const message = isError ? validation.error : validation.result_message;
  const recommendations = isError ? [] : validation.recommendations;

  return (
    <section
      className={`isztar-validation isztar-validation--${variant}`}
      aria-live="polite"
      data-testid="isztar-validation-card"
    >
      <div className="isztar-validation__result">
        <span
          className={`bi ${iconClass} isztar-validation__status-icon`}
          aria-hidden="true"
        />
        <div className="isztar-validation__result-content">
          <span className="isztar-validation__eyebrow">Wynik walidacji</span>
          <p>{message}</p>
        </div>
      </div>

      {recommendations.length > 0 && (
        <div className="isztar-validation__recommendations">
          <div className="isztar-validation__recommendations-heading">
            <span>Rekomendowane kody</span>
            <span className="isztar-validation__count">
              {recommendations.length}
            </span>
          </div>
          <ul className="isztar-validation__list">
            {recommendations.map((recommendation, index) => {
              const copied = copiedCode === recommendation.code;
              const copyFailed = copyErrorCode === recommendation.code;
              const copyLabel = copied
                ? "Skopiowano"
                : copyFailed
                  ? "Spróbuj ponownie"
                  : "Kopiuj";

              return (
                <li key={`${recommendation.code}-${index}`}>
                  <div className="isztar-validation__code-row">
                    <code>{recommendation.code}</code>
                    <button
                      type="button"
                      className={`isztar-validation__copy${
                        copied ? " isztar-validation__copy--success" : ""
                      }${copyFailed ? " isztar-validation__copy--error" : ""}`}
                      onClick={() => handleCopy(recommendation.code)}
                      aria-label={`${copyLabel} kod ${recommendation.code}`}
                    >
                      <span
                        className={`bi ${
                          copied
                            ? "bi-check2"
                            : copyFailed
                              ? "bi-arrow-clockwise"
                              : "bi-copy"
                        }`}
                        aria-hidden="true"
                      />
                      {copyLabel}
                    </button>
                  </div>
                  {recommendation.description && (
                    <p
                      className="isztar-validation__description"
                      title={recommendation.description}
                    >
                      {recommendation.description}
                    </p>
                  )}
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </section>
  );
};

export default IsztarValidationCard;
